/**
 * Qualidade gráfica (mobile-first). Presets LOW/MEDIUM/HIGH controlam DUAS coisas:
 *  (a) render: cap de pixelRatio, sombras, bloom, normal maps, partículas;
 *  (b) 10.9HY — CARGA DE CENA: quantos inimigos vivem, quantos territórios geram horda, a que
 *      distância o cenário some, quantas barras de vida existem e a taxa do minimapa.
 *
 * 10.9HY — DETECÇÃO AUTOMÁTICA (Thiago: "adaptável a qualquer tipo de celular"). Antes o jogo
 * SEMPRE começava em `medium` (pixelRatio 1.5 + sombra + bloom de 4 passes), inclusive num Android
 * de entrada — e só caía depois de 4 s seguidos abaixo de 40 fps. Agora `detectPreset()` escolhe o
 * preset inicial pelo aparelho (memória, núcleos, resolução, toque) e o jogador ainda pode trocar
 * à mão nas Configurações; a escolha manual é salva e tem prioridade sobre a detecção.
 */
import { GameConfig } from "../config/GameConfig";

export type QualityPreset = "low" | "medium" | "high";

export interface QualityValues {
  pixelRatioCap: number;
  shadows: boolean;
  shadowMapSize: number;
  particleScale: number;
  bloom: boolean; // 10.7M — pós-processamento (brilho nos emissivos)
  normalMaps: boolean; // 10.9GQ — relevo do chão (normal map procedural); low desliga
  // 10.9HY — carga de cena (antes tudo fixo no código)
  enemyScale: number; // multiplica o teto de inimigos vivos / por território
  activeTerritories: number; // quantos territórios geram horda ao mesmo tempo
  cullDist: number; // metros até o cenário instanciado sumir
  enemyBars: number; // teto de barras de vida de inimigo na tela
  minimapHz: number; // redesenhos do minimapa por segundo
  decorShadows: boolean; // cenário (árvore/pedra/muro) projeta sombra?
  /** 10.9HZ — multiplica a densidade da névoa. O corte de cenário mais curto (cullDist) só é
   *  invisível se a névoa esconder a distância do corte: com densidade 0,0085 sobra ~20% de
   *  contraste a 150 m, mas ~59% a 85 m (o chunk piscava à vista). No preset baixo a névoa fecha
   *  mais, devolvendo o corte pra dentro do "invisível" — e ainda economiza overdraw. */
  fogBoost: number;
  /** 10.9IE — resolução do render como FRAÇÃO do que a tela pede. Medido: a CPU do jogo está
   *  livre (0,3 ms/quadro) e TODO o custo é render — então num aparelho fraco o que resolve é
   *  desenhar menos pixels. 0.8 = 36% menos pixels que 1.0, com upscale do próprio canvas. */
  renderScale: number;
  /** 10.9IE — lado da textura procedural de chão (512 = original; 256 gera 4× mais rápido). */
  groundTexSize: number;
}

/** 10.9HY — sinais do aparelho (só leitura; nenhum deles existe em todo navegador). */
export interface DeviceProbe {
  memoryGb: number | null; // navigator.deviceMemory (Chrome/Android)
  cores: number | null; // navigator.hardwareConcurrency
  dpr: number;
  pixels: number; // pixels físicos da tela (largura × altura × dpr²)
  touch: boolean;
  /** 10.9IG — rodando dentro da WebView do Android (APK) em vez do navegador. */
  webview: boolean;
  reason: string; // por que caiu nesse preset (vai pro painel de debug)
}

/** Esta rodando dentro do app nativo (APK)? So o Capacitor sabe responder isso. */
function nativo(): boolean {
  const c = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return typeof c?.isNativePlatform === "function" ? c.isNativePlatform() : false;
}

export function probeDevice(): DeviceProbe {
  const nav = navigator as Navigator & { deviceMemory?: number; hardwareConcurrency?: number };
  const dpr = Math.max(1, window.devicePixelRatio || 1);
  const w = window.screen?.width ?? window.innerWidth;
  const h = window.screen?.height ?? window.innerHeight;
  const touch = (navigator.maxTouchPoints ?? 0) > 0 || matchMedia("(pointer: coarse)").matches;
  // 10.9IG — Thiago: "é até fluido no navegador mas pra celular precisa melhorar". O APK carrega a
  // MESMA página publicada, então não é o jogo que muda: é o MOTOR. O app roda na WebView do
  // sistema, que renderiza bem mais devagar que o Chrome no mesmo aparelho. A WebView se declara
  // com "; wv)" no user agent. Sem isso, um celular bom ganhava preset "high" também dentro do
  // app, e engasgava. O teste fica ANCORADO no separador: um "wv" solto casaria com qualquer
  // aparelho que tenha essas duas letras no nome do modelo (o modelo vai no user agent).
  // O Capacitor responde se esta rodando NATIVO — cobre a WebView do iOS, que nao se declara no
  // user agent. Atencao: `window.Capacitor` EXISTE tambem no navegador (o pacote define o objeto
  // ao ser importado), entao testar a existencia dele derrubaria a qualidade de todo mundo na
  // web. O que vale e `isNativePlatform()`.
  const ua = navigator.userAgent || "";
  const webview =
    /[;(]\s*wv[;)]/i.test(ua) || nativo();
  return {
    memoryGb: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
    cores: typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null,
    dpr,
    pixels: Math.round(w * h * dpr * dpr),
    touch,
    webview,
    reason: "",
  };
}

/**
 * Escolhe o preset inicial pelo aparelho. Conservador de propósito: na dúvida cai pra BAIXO —
 * é muito melhor abrir leve e o jogador subir a qualidade do que abrir travando. O governador de
 * fps (Game) ainda derruba mais se mesmo assim não der conta.
 */
const ABAIXO: Record<QualityPreset, QualityPreset> = { high: "medium", medium: "low", low: "low" };

export function detectPreset(p: DeviceProbe = probeDevice()): { preset: QualityPreset; reason: string } {
  const r = detectBase(p);
  // 10.9IG — dentro do APK (WebView) desce UM degrau: mesmo aparelho, motor mais lento.
  if (p.webview && r.preset !== "low") return { preset: ABAIXO[r.preset], reason: `${r.reason} · app (WebView)` };
  return r;
}

function detectBase(p: DeviceProbe): { preset: QualityPreset; reason: string } {
  // Desktop (sem toque): assume folga — telas grandes e GPU dedicada são a regra.
  if (!p.touch) return { preset: "high", reason: "desktop (sem toque)" };

  // `deviceMemory` é o sinal mais confiável no Android. Até 3 GB = entrada (vai de LOW);
  // 4 GB é a fronteira: médio, a não ser que a tela seja densa (aí o custo é de pixel).
  if (p.memoryGb !== null) {
    if (p.memoryGb <= 3) return { preset: "low", reason: `${p.memoryGb} GB de RAM` };
    if (p.memoryGb <= 4) {
      // 4 GB com tela muito densa (1080p+ em dpr 3) ainda sofre: o custo é de PIXEL, não de RAM.
      if (p.pixels > 2_500_000) return { preset: "low", reason: `${p.memoryGb} GB + tela densa` };
      return { preset: "medium", reason: `${p.memoryGb} GB de RAM` };
    }
    if (p.memoryGb >= 8 && (p.cores ?? 0) >= 8) return { preset: "high", reason: `${p.memoryGb} GB + ${p.cores} núcleos` };
    return { preset: "medium", reason: `${p.memoryGb} GB de RAM` };
  }

  // Sem deviceMemory (iOS/Safari e alguns Android): decide por núcleos + densidade de tela.
  const cores = p.cores ?? 4;
  if (cores <= 4) return { preset: "low", reason: `${cores} núcleos (sem leitura de RAM)` };
  if (cores <= 6 || p.pixels > 3_000_000) return { preset: "medium", reason: `${cores} núcleos / tela densa` };
  return { preset: "high", reason: `${cores} núcleos` };
}

class QualityManager {
  private preset: QualityPreset = GameConfig.quality.default as QualityPreset;
  /** Como o preset atual foi escolhido (detecção automática ou escolha do jogador). */
  detectedReason = "";
  auto = false;

  get name(): QualityPreset {
    return this.preset;
  }

  get values(): QualityValues {
    return GameConfig.quality.presets[this.preset];
  }

  setPreset(p: QualityPreset): void {
    this.preset = p;
    this.auto = false;
  }

  /** 10.9HY — usado no boot quando o save NÃO tem escolha manual de gráficos. */
  applyDetected(): { preset: QualityPreset; reason: string } {
    const r = detectPreset();
    this.preset = r.preset;
    this.detectedReason = r.reason;
    this.auto = true;
    return r;
  }
}

export const quality = new QualityManager();
